import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import FormalBannerImage from "../../../../assets/technologies/tools-and-languages/uvm/uvmbannerimage.png";
import ProfileImage from "../../../../assets/blogpages/person.png";
import "./FormalVerification.css";
import { AnimatePresence, motion } from "framer-motion";

// Simple highlight component: wraps matches in <mark>
const Highlight = ({ text = "", query = "" }) => {
  if (!query) return <>{text}</>;
  const parts = text.split(new RegExp(`(${query})`, "gi"));
  return parts.map((part, i) =>
    part.toLowerCase() === query.toLowerCase()
      ? <mark key={i}>{part}</mark>
      : part
  );
};

const FormalVerification = () => {
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [showThankYou, setShowThankYou] = useState(false);
  const closeOverlay = () => setShowThankYou(false);

  const [replyVisible, setReplyVisible] = useState({});
  const toggleReply = (id) =>
    setReplyVisible(prev => ({ ...prev, [id]: !prev[id] }));

  // refs for scrolling
  const sectionRefs = {
    intro: useRef(null),
    theory: useRef(null),
    assertions: useRef(null),
    modelChecking: useRef(null),
    eqChecking: useRef(null),
    induction: useRef(null),
    coverage: useRef(null),
    toolIntegration: useRef(null),
    challenges: useRef(null),
    trends: useRef(null),
    comments: useRef(null),
  };

  const scrollTo = ref =>
    ref.current && ref.current.scrollIntoView({ behavior: "smooth", block: "center" });

  const handleSearch = () => setSearchTerm(searchInput.trim());
  useEffect(() => {
    if (!searchTerm) return;
    const firstMark = document.querySelector("mark");
    firstMark?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [searchTerm]);

  // fade‐in/out on scroll
  useEffect(() => {
    const io = new IntersectionObserver(
      entries =>
        entries.forEach(e =>
          e.isIntersecting
            ? e.target.classList.add("visible")
            : e.target.classList.remove("visible")
        ),
      { threshold: 0.1 }
    );
    document.querySelectorAll(".fade-section").forEach(el => io.observe(el));
    return () => io.disconnect();
  }, []);

  // auto‐dismiss popup
  useEffect(() => {
    if (showThankYou) {
      const t = setTimeout(() => setShowThankYou(false), 2000);
      return () => clearTimeout(t);
    }
  }, [showThankYou]);

  const handleCommentSubmit = e => {
    e.preventDefault();
    setShowThankYou(true);
    e.target.reset();
  };

  return (
    <div className="blog-post-page formal-verification">
      {/* Banner */}
      <div className="blog-banner fade-section">
        <div className="banner-overlay">
          <h1>Formal Verification</h1>
          <div className="breadcrumb">
            <Link to="/services/asic-verification">Services</Link> | Formal Verification
          </div>
        </div>
      </div>

      {/* Mobile nav */}
      <div className="mobile-nav fade-section">
        <div className="search-box">
          <input
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            type="text"
            placeholder="Search keyword..."
            className="search-input"
          />
          <button onClick={handleSearch} className="search-button">
            Search
          </button>
        </div>
        <div className="table-of-contents">
          <h4>In This Article</h4>
          <ul>
            {Object.entries(sectionRefs).map(([key, ref]) => (
              <li key={key}>
                <button onClick={() => scrollTo(ref)}>
                  {{
                    intro: "Introduction",
                    theory: "Theory & Foundations",
                    assertions: "Assertions & PSL/SVA",
                    modelChecking: "Model Checking",
                    eqChecking: "Equivalence Checking",
                    induction: "Induction & Theorem Proving",
                    coverage: "Formal Coverage Metrics",
                    toolIntegration: "Tool Flows & Integration",
                    challenges: "Scalability & Abstraction",
                    trends: "Future Trends",
                    comments: "Comments",
                  }[key]}
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Content */}
      <div className="blog-content-container">
        <div className="blog-left-column">
          <img
            className="post-main-image fade-section"
            src={FormalBannerImage}
            alt="Formal Verification"
          />
          <div className="post-info-row fade-section">
            <span className="post-author">By Jane Doe</span>
            <span className="post-category">Category: ASIC Verification</span>
            <button className="comment-link" onClick={() => scrollTo(sectionRefs.comments)}>
              2 Comments
            </button>
          </div>

          {/* 1. Introduction */}
          <h2 ref={sectionRefs.intro} className="fade-section">Introduction to Formal Verification</h2>
          <p className="fade-section">
            <Highlight
              text="Formal verification uses mathematical techniques to prove or disprove the correctness of hardware designs exhaustively, without relying on simulation alone."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Unlike simulation, which tests a finite number of vectors, formal methods explore all reachable states of the design to guarantee that specified properties hold under every scenario."
              query={searchTerm}
            />
          </p>

          {/* 2. Theory & Foundations */}
          <h2 ref={sectionRefs.theory} className="fade-section">Theory & Foundations</h2>
          <p className="fade-section">
            <Highlight
              text="At its core, formal verification translates hardware RTL into mathematical models—state machines, transition relations, and temporal logic formulas."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Temporal logics like Linear Temporal Logic (LTL) and Computation Tree Logic (CTL) express properties over sequences of states, enabling assertions such as 'eventually acknowledge is asserted after request.'"
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Core algorithms include fixed‐point iterations, satisfiability (SAT/SMT) solving, and Binary Decision Diagrams (BDDs) for symbolic state space representation."
              query={searchTerm}
            />
          </p>

          {/* 3. Assertions & PSL/SVA */}
          <h2 ref={sectionRefs.assertions} className="fade-section">Assertions & PSL/SVA</h2>
          <p className="fade-section">
            <Highlight
              text="Property Specification Language (PSL) and SystemVerilog Assertions (SVA) let engineers embed properties directly in RTL, capturing intended behavior as reusable checks."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Assertions monitor events and signal sequences—e.g. ‘always @(posedge clk) req |=> ##[1:3] grant’, specifying that a grant must follow request within 1–3 cycles."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Coverage-driven assertion validation measures which properties have been exercised, guiding development to fill gaps in specification."
              query={searchTerm}
            />
          </p>

          {/* 4. Model Checking */}
          <h2 ref={sectionRefs.modelChecking} className="fade-section">Model Checking</h2>
          <p className="fade-section">
            <Highlight
              text="Model checking exhaustively explores the state graph symbolically or explicitly to prove if a given property holds or find a counterexample trace."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Symbolic Model Checking uses BDDs or SAT-based techniques to handle large state spaces, while Bounded Model Checking (BMC) searches up to a fixed depth using SAT/SMT solvers."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Counterexamples from model checkers provide concrete waveforms for debugging, pinpointing the exact sequence violating a property."
              query={searchTerm}
            />
          </p>

          {/* 5. Equivalence Checking */}
          <h2 ref={sectionRefs.eqChecking} className="fade-section">Equivalence Checking</h2>
          <p className="fade-section">
            <Highlight
              text="Equivalence Checking (EC) verifies that two representations of a design—RTL vs synthesized netlist—are functionally identical."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Formal EC tools build miter circuits, connecting outputs of both versions and checking that all outputs match for every input combination."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="EC catches synthesis bugs, unintentional optimizations, and tool-specific transformations that break functional equivalence."
              query={searchTerm}
            />
          </p>

          {/* 6. Induction & Theorem Proving */}
          <h2 ref={sectionRefs.induction} className="fade-section">Induction & Theorem Proving</h2>
          <p className="fade-section">
            <Highlight
              text="k-Induction extends model checking by proving properties hold for k steps and preserved under inductive hypotheses for all subsequent steps."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Theorem provers allow user‐guided proofs for complex properties, leveraging interactive tactics and rewriting rules to establish correctness."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Combining induction with abstraction reduces proof complexity, enabling verification of large blocks by simplifying irrelevant details."
              query={searchTerm}
            />
          </p>

          {/* 7. Formal Coverage Metrics */}
          <h2 ref={sectionRefs.coverage} className="fade-section">Formal Coverage Metrics</h2>
          <p className="fade-section">
            <Highlight
              text="Coverage metrics—proof coverage, assertion coverage, and abstraction coverage—measure how thoroughly properties and design aspects have been exercised."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Functional coverage targets, borrowed from simulation, can be adapted to formal flows to check state space exploration of critical scenarios."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Coverage gaps highlight missing properties or unreachable states, guiding engineers to refine specifications or add auxiliary checks."
              query={searchTerm}
            />
          </p>

          {/* 8. Tool Flows & Integration */}
          <h2 ref={sectionRefs.toolIntegration} className="fade-section">Tool Flows & Integration</h2>
          <p className="fade-section">
            <Highlight
              text="Modern EDA suites integrate formal engines into regression flows—co-running simulation, lint, and formal to provide immediate feedback on new code commits."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="IP-XACT, YAML, or Tcl scripts automate property extraction, netlist translation, and environment setup for scalable formal runs across multiple blocks."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Cloud-based formal platforms enable parallel exploration of properties, sharing proof results and counterexamples across global teams."
              query={searchTerm}
            />
          </p>

          {/* 9. Scalability & Abstraction */}
          <h2 ref={sectionRefs.challenges} className="fade-section">Scalability & Abstraction Challenges</h2>
          <p className="fade-section">
            <Highlight
              text="State explosion is the primary barrier to scaling formal—abstraction techniques such as slicing, cone‐of‐influence reduction, and assume‐guarantee reasoning mitigate complexity."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Abstraction refinement loops automatically add details only where needed, focusing proof effort on relevant signals."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Compositional verification breaks the design into smaller modules, verifying each in isolation under well‐defined interfaces."
              query={searchTerm}
            />
          </p>

          {/* 10. Future Trends */}
          <h2 ref={sectionRefs.trends} className="fade-section">Future Trends in Formal Verification</h2>
          <p className="fade-section">
            <Highlight
              text="Machine-learning–driven proof guidance suggests inductive invariants and lemmas based on past proof data, reducing manual effort."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Expansion of formal techniques into analog/mixed‐signal domains and tighter integration with emulation and FPGA-in-the-loop verification is emerging."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Standardization efforts (e.g., Accellera’s Formal Verification Working Group) aim to harmonize property languages and coverage definitions across tools."
              query={searchTerm}
            />
          </p>

          {/* Comment Section */}
          <div className="comment-section" ref={sectionRefs.comments}>
            <h3>2 Comments</h3>

            {/* Comment 1 */}
            <div className="comment-card fade-section">
              <div className="comment-header">
                <img src={ProfileImage} alt="User" className="comment-profile-img" />
                <div>
                  <div className="comment-author-date">
                    <span className="comment-author">Verity Expert</span>
                    <span className="comment-date">May 1, 2025</span>
                  </div>
                </div>
              </div>
              <p className="comment-text">
                Excellent coverage of model checking and induction methods—very informative!
              </p>
              <button className="reply-button" onClick={() => toggleReply("verity")}>
                Reply
              </button>
              <AnimatePresence initial={false}>
                {replyVisible["verity"] && (
                  <motion.div
                    className="leave-comment"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <h3>Reply to Verity Expert</h3>
                    <form onSubmit={handleCommentSubmit}>
                      <div className="form-group">
                        <label>Name</label>
                        <input type="text" placeholder="Your name..." required />
                      </div>
                      <div className="form-group">
                        <label>Message</label>
                        <textarea placeholder="Your reply..." required />
                      </div>
                      <button type="submit" className="send-message-btn">
                        Reply
                      </button>
                    </form>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Comment 2 */}
            <div className="comment-card fade-section">
              <div className="comment-header">
                <img src={ProfileImage} alt="User" className="comment-profile-img" />
                <div>
                  <div className="comment-author-date">
                    <span className="comment-author">Logic Guru</span>
                    <span className="comment-date">May 2, 2025</span>
                  </div>
                </div>
              </div>
              <p className="comment-text">
                Insightful explanation of formal coverage—helped clarify best practices.
              </p>
              <button className="reply-button" onClick={() => toggleReply("logic")}>
                Reply
              </button>
              <AnimatePresence initial={false}>
                {replyVisible["logic"] && (
                  <motion.div
                    className="leave-comment"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <h3>Reply to Logic Guru</h3>
                    <form onSubmit={handleCommentSubmit}>
                      <div className="form-group">
                        <label>Name</label>
                        <input type="text" placeholder="Your name..." required />
                      </div>
                      <div className="form-group">
                        <label>Message</label>
                        <textarea placeholder="Your reply..." required />
                      </div>
                      <button type="submit" className="send-message-btn">
                        Reply
                      </button>
                    </form>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Leave a comment */}
            <div className="leave-comment fade-section">
              <h3>Leave a comment</h3>
              <p>Have questions or feedback? Fill out the form below!</p>
              <form onSubmit={handleCommentSubmit}>
                <div className="form-group">
                  <label>Name</label>
                  <input type="text" placeholder="Name here..." required />
                </div>
                <div className="form-group">
                  <label>Message</label>
                  <textarea placeholder="Message here…" required />
                </div>
                <button type="submit" className="send-message-btn">
                  Send Message
                </button>
              </form>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="blog-right-column fade-section">
          <div className="search-box">
            <input
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              type="text"
              placeholder="Search keyword..."
              className="search-input"
            />
            <button onClick={handleSearch} className="search-button">
              Search
            </button>
          </div>
          <div className="table-of-contents">
            <h4>In This Article</h4>
            <ul>
              {Object.entries(sectionRefs).map(([key, ref]) => (
                <li key={key}>
                  <button onClick={() => scrollTo(ref)}>
                    {{
                      intro: "Introduction",
                      theory: "Theory & Foundations",
                      assertions: "Assertions & PSL/SVA",
                      modelChecking: "Model Checking",
                      eqChecking: "Equivalence Checking",
                      induction: "Induction & Theorem Proving",
                      coverage: "Formal Coverage Metrics",
                      toolIntegration: "Tool Flows & Integration",
                      challenges: "Scalability & Abstraction",
                      trends: "Future Trends",
                      comments: "Comments",
                    }[key]}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Thank You Popup */}
        <AnimatePresence>
          {showThankYou && (
            <motion.div
              className="thank-you-overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={closeOverlay}
            >
              <motion.div
                className="thank-you-content"
                initial={{ scale: 0.8 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.8 }}
                transition={{ type: "spring", stiffness: 300 }}
                onClick={e => e.stopPropagation()}
              >
                <div className="checkmark-container">
                  <motion.svg
                    className="checkmark"
                    viewBox="0 0 52 52"
                    initial={{ pathLength: 0 }}
                    animate={{ pathLength: 1 }}
                    transition={{ duration: 1, ease: "easeInOut" }}
                  >
                    <circle cx="26" cy="26" r="25" stroke="#007bff" strokeWidth="2" />
                    <path
                      d="M16 27.5L23 34.5L36 19.5"
                      stroke="#007bff"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </motion.svg>
                </div>
                <h3>Thank you for the feedback!</h3>
                <p>We appreciate your comment.</p>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default FormalVerification;